import React from 'react';
import styled, { css } from 'styled-components';
import { BorderRadiusLeft, BorderRadiusRight } from '../Common/BorderRadius';
import { Button, JRCButton } from '../JRCButton/JRCButton';
import { DashedFocusBorder } from '../Common/DashedFocusBorder';
import type { ButtonProps, MenuWithTitleProps, OpenDirection, DropdownOptionProps } from './JRCButtonDropdown.types';
import { ColorsOrShades } from 'styles/theme';
import { ButtonType } from '../JRCButton/JRCButtonConfig';

const CustomMarginCss = css<ButtonProps>`
    margin: 0;
    ${(props) => props.borderRight && 'margin-left: -2px'};

    ${(props) => props.borderLeft && BorderRadiusLeft};
    ${(props) => props.borderRight && BorderRadiusRight};
    ${(props) => props.open && 'border-bottom-left-radius: 0'};
    ${(props) => props.open && 'border-bottom-right-radius: 0'};
    ${(props) =>
        props.darker &&
        `background-color: ${props.theme.color[props.$themeButton.active.background as ColorsOrShades]}`};
    ${(props) =>
        props.darker && `border-color: ${props.theme.color[props.$themeButton.active.border as ColorsOrShades]}`};
`;

export const MarginLessJRCButton = styled(JRCButton)<ButtonProps>`
    ${CustomMarginCss};
`;

const MarginLessButton = styled(Button)<ButtonProps & OpenDirection>`
    ${CustomMarginCss};
    border-radius: ${(props) => (props.direction === 'right' ? '0 8px' : '8px 0')} 0 0;
    cursor: default;
    ${(props) => props.direction === 'right' && 'border-left: none'};
    ${(props) => props.direction === 'left' && 'border-right: none'};
    flex: 1;
    display: block;
`;

export const MenuWithoutTitle = styled.div`
    position: absolute;
    z-index: 10;
    top: 100%;
    width: 100%;
    background-color: ${(props) => props.theme.color.white};
    border-radius: 0 0 8px 8px;
    box-shadow: 1px 2px 3px ${(props) => props.theme.color.grey2}, -1px 2px 3px ${(props) => props.theme.color.grey2};
`;

const Menu = styled(MenuWithoutTitle)`
    pointer-events: all;
    position: unset;
    box-shadow: none;
`;

const MenuWithTitleWrapper = styled.div<OpenDirection>`
    position: absolute;
    z-index: 10;
    top: 0;
    ${(props) => props.direction === 'right' && 'left: 0'};
    ${(props) => props.direction === 'left' && 'right: 0'};
    min-width: 100%;
    width: max-content;
    border-radius: 8px;
    box-shadow: 0 0 5px 1.5px ${(props) => props.theme.color.grey2};
    pointer-events: none;
    background: rgb(255, 255, 255);
`;

const FloatingTitle = styled.div<OpenDirection>`
    display: flex;
    flex-direction: ${(props) => (props.direction === 'right' ? 'row-reverse' : 'row')};
    pointer-events: none;
`;

const FakeIcon = styled.div`
    pointer-events: none;
    width: 43px;
    height: 40px;
`;

export const MenuWithTitle = ({ direction, label, themeButton, children }: MenuWithTitleProps) => {
    const theme: ButtonType = {
        color: themeButton.color,
        background: themeButton.background,
        border: themeButton.border,
        active: {
            background: themeButton.background,
            border: themeButton.border,
        },
        hover: {
            background: themeButton.background,
            border: themeButton.border,
        },
    };

    return (
        <MenuWithTitleWrapper direction={direction}>
            <FloatingTitle direction={direction}>
                <MarginLessButton $themeButton={theme} hasLabel as="div" direction={direction}>
                    {label}
                </MarginLessButton>
                <FakeIcon />
            </FloatingTitle>
            <Menu>{children}</Menu>
        </MenuWithTitleWrapper>
    );
};

export const DropdownOption = styled.button<DropdownOptionProps>`
    display: flex;
    box-sizing: border-box;
    width: 100%;
    flex-direction: row;
    gap: 8px;
    align-items: center;
    padding: 8px 16px 8px 8px;
    min-height: 40px;
    line-height: 1.25;
    cursor: ${(props) => (!props.disabled ? 'pointer' : 'inherit')};
    border-bottom: 1px solid ${(props) => props.theme.color.grey2};
    &:last-child {
        border-bottom: 0;
        border-radius: 0 0 8px 8px;
    }
    &:hover,
    &:focus-visible {
        background-color: ${(props) => !props.disabled && props.theme.color.grey1};
        &:last-child {
            border-radius: 0 0 8px 8px;
        }
        text-decoration: none;
        color: ${(props) => (!props.disabled ? 'inherit' : props.theme.color.grey4)};
    }
    label {
        cursor: ${(props) => (!props.disabled ? 'pointer' : 'inherit')};
    }
    background-color: ${(props) =>
        !props.disabled ? (props.optionSelected ? props.theme.color.grey1 : 'inherit') : props.theme.color.grey2};
    color: ${(props) => props.disabled && props.theme.color.grey4};
    text-decoration: ${(props) => (props.optionSelected ? 'none' : 'inherit')};
    ${DashedFocusBorder()}
`;
